import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { getPnL, type PnLItem, type PnLSliceOut, type PnLSummaryOut } from '@/lib/api'
import { t as translate } from '@/i18n'
import { useLocale } from '@/i18n/LocaleProvider'
import { formatDateTime, formatDay, formatMoney, formatPct, formatQty } from '@/lib/format'

type Period = 'today' | '7d' | '30d' | 'all'

const PERIODS = [
  ['today', 'period.today'],
  ['7d', 'period.7d'],
  ['30d', 'period.30days'],
  ['all', 'period.all'],
] as const

export function Profit() {
  const { t } = useLocale()
  const [period, setPeriod] = useState<Period>('30d')
  const pnl = useQuery({
    queryKey: ['pnl', period],
    queryFn: () => getPnL(undefined, undefined, period),
  })
  const data = pnl.data

  return (
    <div className="mx-auto max-w-6xl px-4 py-5 md:px-8 md:py-8">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">{t('pnl.title')}</h1>
          <p className="mt-1 text-sm text-slate-500">{t('pnl.lead')}</p>
          {data ? (
            <p className="mt-1 text-xs text-slate-600">
              {formatDay(data.from_at)} — {formatDay(data.to_at)}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-1">
          {PERIODS.map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setPeriod(id)}
              className={[
                'min-h-11 rounded-md px-3 text-sm',
                period === id ? 'bg-slate-100 text-slate-900' : 'border border-surface-border text-slate-400',
              ].join(' ')}
            >
              {t(label)}
            </button>
          ))}
        </div>
      </div>

      {pnl.isLoading && <p className="mt-6 text-sm text-slate-500">{t('pnl.counting')}</p>}
      {pnl.isError && (
        <p className="mt-6 text-sm text-loss">{pnl.error instanceof Error ? pnl.error.message : t('common.error')}</p>
      )}

      {data?.recalculating?.length ? (
        <p className="mt-4 text-sm text-pending">{t('pnl.recalc', { items: data.recalculating.join(', ') })}</p>
      ) : null}

      {data && (
        <>
          <Hero data={data} />
          <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label={t('pnl.sells')} value={String(data.sells)} />
            <Stat label={t('pnl.revenue', { ccy: data.primary_code })} value={formatMoney(data.revenue_primary, data.primary_code)} />
            <Stat label={t('pnl.fees', { ccy: data.primary_code })} value={formatMoney(data.fees_primary, data.primary_code)} />
            <Stat label={t('pnl.avgMargin')} value={formatPct(data.avg_margin_pct)} profit={Number(data.avg_margin_pct ?? 0)} />
          </div>
          <ExtraMethods data={data} />
          {data.uncovered_sells > 0 && (
            <p className="mt-4 text-sm text-pending">
              {t('pnl.uncovered', { n: data.uncovered_sells })}
            </p>
          )}

          <section className="mt-6 rounded-lg border border-surface-border bg-surface-raised p-4">
            <h2 className="text-sm font-medium text-slate-300">{t('pnl.byDay', { ccy: data.primary_code })}</h2>
            <div className="mt-3">
              <BarChart
                points={data.series.map((row) => ({
                  label: row.day.slice(5),
                  value: Number(row.profit_fifo),
                }))}
              />
            </div>
          </section>

          <Extremes data={data} />

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Slice title={t('pnl.sliceEx')} rows={data.by_exchange} currency={data.primary_code} />
            <Slice title={t('pnl.sliceFiat')} rows={data.by_fiat} currency={data.primary_code} />
            <Slice title={t('pnl.sliceBank')} rows={data.by_bank} currency={data.primary_code} />
            <Slice title={t('pnl.sliceClient')} rows={data.by_counterparty} currency={data.primary_code} />
          </div>

          <h2 className="mt-8 text-sm font-medium text-slate-300">{t('pnl.sales')}</h2>
          <ul className="mt-3 space-y-2 md:hidden">
            {data.items.map((item) => (
              <li key={item.id}>
                <DealCard item={item} highlight={badgeFor(item, data)} />
              </li>
            ))}
          </ul>
          <ul className="mt-3 hidden divide-y divide-surface-border rounded-lg border border-surface-border bg-surface-raised md:block">
            {data.items.map((item) => (
              <li key={item.id}>
                <DealRow item={item} highlight={badgeFor(item, data)} />
              </li>
            ))}
          </ul>
          {data.items.length === 0 && (
            <p className="mt-4 rounded-lg border border-dashed border-surface-border px-4 py-8 text-center text-sm text-slate-500">
              {t('pnl.noSales')}
            </p>
          )}
        </>
      )}
    </div>
  )
}

function Hero({ data }: { data: PnLSummaryOut }) {
  const current = Number(data.profit_primary ?? data.profit_fifo)
  const previous = data.previous_profit_primary == null ? null : Number(data.previous_profit_primary)
  const delta = previous == null ? null : current - previous
  return (
    <div className="mt-6 rounded-lg border border-surface-border bg-surface-raised p-4">
      <div className="text-xs text-slate-500">{translate('pnl.fifo', { ccy: data.primary_code })}</div>
      <div className={['num mt-1 text-2xl', current > 0 ? 'text-profit' : current < 0 ? 'text-loss' : 'text-slate-100'].join(' ')}>
        {formatMoney(data.profit_primary ?? data.profit_fifo, data.primary_code)}
      </div>
      {delta != null && data.previous_sells != null ? (
        <p className="mt-2 text-xs text-slate-500">
          {translate('pnl.prev', {
            money: formatMoney(data.previous_profit_primary ?? '0', data.primary_code),
            n: data.previous_sells,
          })}{' '}
          <span className={delta > 0 ? 'text-profit' : delta < 0 ? 'text-loss' : 'text-slate-400'}>
            {delta > 0 ? '+' : ''}
            {formatMoney(delta, data.primary_code)}
          </span>
        </p>
      ) : (
        <p className="mt-2 text-xs text-slate-600">{translate('pnl.noAll')}</p>
      )}
    </div>
  )
}

function ExtraMethods({ data }: { data: PnLSummaryOut }) {
  const fifo = Number(data.profit_fifo)
  const wac = Number(data.profit_wac)
  const showWac = Math.abs(fifo - wac) >= 0.02
  const showSecondary = Boolean(data.secondary_code && data.profit_secondary != null)
  if (!showWac && !showSecondary) return null
  return (
    <div className="mt-3 grid grid-cols-2 gap-3">
      {showWac && (
        <Stat
          label={translate('pnl.wac')}
          value={formatMoney(data.profit_wac, data.currency_code ?? data.primary_code)}
          profit={wac}
        />
      )}
      {showSecondary && (
        <Stat
          label={data.secondary_code ?? translate('pnl.second')}
          value={formatMoney(data.profit_secondary ?? '0', data.secondary_code ?? undefined)}
          profit={Number(data.profit_secondary ?? 0)}
        />
      )}
    </div>
  )
}

function Extremes({ data }: { data: PnLSummaryOut }) {
  const best = data.items.find((item) => item.order_id === data.best_order_id)
  const worst = data.items.find((item) => item.order_id === data.worst_order_id)
  if (!best && !worst) return null
  return (
    <div className="mt-4 grid gap-3 sm:grid-cols-2">
      {best && (
        <Link
          to={`/orders/${best.order_id}`}
          className="rounded-lg border border-surface-border bg-surface-raised p-4"
        >
          <div className="text-xs text-slate-500">{translate('pnl.best')}</div>
          <div className="mt-1 text-sm text-slate-200">{formatDateTime(best.occurred_at)}</div>
          <div className="num mt-1 text-profit">
            {formatMoney(best.net_profit_fifo ?? '0', best.currency_code)}
          </div>
        </Link>
      )}
      {worst && worst.order_id !== best?.order_id && (
        <Link
          to={`/orders/${worst.order_id}`}
          className="rounded-lg border border-surface-border bg-surface-raised p-4"
        >
          <div className="text-xs text-slate-500">{translate('pnl.worst')}</div>
          <div className="mt-1 text-sm text-slate-200">{formatDateTime(worst.occurred_at)}</div>
          <div
            className={['num mt-1', Number(worst.net_profit_fifo ?? 0) < 0 ? 'text-loss' : 'text-slate-200'].join(' ')}
          >
            {formatMoney(worst.net_profit_fifo ?? '0', worst.currency_code)}
          </div>
        </Link>
      )}
    </div>
  )
}

function Slice({ title, rows, currency }: { title: string; rows: PnLSliceOut[]; currency: string }) {
  return (
    <section className="rounded-lg border border-surface-border bg-surface-raised p-4">
      <h2 className="text-sm font-medium text-slate-300">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">{translate('common.empty')}</p>
      ) : (
        <table className="mt-3 w-full text-sm">
          <thead className="text-left text-xs text-slate-500">
            <tr>
              <th className="py-1">{translate('dash.slice')}</th>
              <th className="py-1 text-right">{translate('pnl.soldShort')}</th>
              <th className="py-1 text-right">{translate('pnl.revCol')}</th>
              <th className="py-1 text-right">{translate('nav.profit')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-t border-surface-border/60">
                <td className="py-1.5">{row.label}</td>
                <td className="num py-1.5 text-right">{row.sells}</td>
                <td className="num py-1.5 text-right">{formatMoney(row.revenue_primary, currency)}</td>
                <td className="num py-1.5 text-right">{formatMoney(row.profit_fifo, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function DealCard({ item, highlight }: { item: PnLItem; highlight: string | null }) {
  const profit = Number(item.net_profit_fifo ?? 0)
  return (
    <Link to={`/orders/${item.order_id}`} className="block rounded-lg border border-surface-border bg-surface-raised p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm text-slate-200">{formatDateTime(item.occurred_at)}</div>
          <div className="mt-0.5 text-xs text-slate-500">
            {item.exchange_code ?? '—'}
            {item.counterparty_nickname ? ` · ${item.counterparty_nickname}` : ''}
            {highlight ? ` · ${highlight}` : ''}
          </div>
        </div>
        <span className={['num text-sm', profit < 0 ? 'text-loss' : 'text-profit'].join(' ')}>
          {formatMoney(item.net_profit_fifo ?? '0', item.currency_code)}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap justify-between gap-2 text-xs text-slate-500">
        <span className={item.side === 'buy' ? 'text-profit' : 'text-loss'}>
          {item.side ? translate(item.side === 'buy' ? 'side.buy' : 'side.sell') : translate('side.sell')}
          {item.quantity && item.asset_code ? ` · ${formatQty(item.quantity)} ${item.asset_code}` : ''}
        </span>
        <span>{translate('pnl.cost', { value: formatMoney(item.cost_basis_fifo ?? '0', item.currency_code) })}</span>
      </div>
      <div className="mt-1 flex justify-between text-xs text-slate-500">
        <span>{translate('pnl.rev', { value: formatMoney(item.revenue, item.currency_code) })}</span>
        <span>{translate('pnl.margin', { value: formatPct(item.margin_pct_fifo) })}</span>
      </div>
    </Link>
  )
}

function DealRow({ item, highlight }: { item: PnLItem; highlight: string | null }) {
  const profit = Number(item.net_profit_fifo ?? 0)
  return (
    <Link to={`/orders/${item.order_id}`} className="flex items-center justify-between gap-4 px-4 py-3 text-sm hover:bg-slate-700/20">
      <div>
        <div className="text-slate-200">
          {formatDateTime(item.occurred_at)}
          {highlight ? <span className="ml-2 text-[11px] text-slate-500">{highlight}</span> : null}
        </div>
        <div className="text-xs text-slate-500">
          {item.side ? translate(item.side === 'buy' ? 'side.buy' : 'side.sell') : translate('side.sell')}
          {item.quantity && item.asset_code ? ` · ${formatQty(item.quantity)} ${item.asset_code}` : ''}
          {item.exchange_code ? ` · ${item.exchange_code}` : ''}
          {item.bank_name ? ` · ${item.bank_name}` : ''}
          {item.counterparty_nickname ? ` · ${item.counterparty_nickname}` : ''}
        </div>
        <div className="text-xs text-slate-600">
          {translate('pnl.rev', { value: formatMoney(item.revenue, item.currency_code) })}
          {' · '}
          {translate('pnl.cost', { value: formatMoney(item.cost_basis_fifo ?? '0', item.currency_code) })}
          {item.warnings.length ? ` · ${item.warnings[0]}` : ''}
        </div>
      </div>
      <div className="num shrink-0 text-right">
        <div className={profit < 0 ? 'text-loss' : 'text-profit'}>
          {formatMoney(item.net_profit_fifo ?? '0', item.currency_code)}
        </div>
        <div className="text-xs text-slate-500">{translate('pnl.margin', { value: formatPct(item.margin_pct_fifo) })}</div>
      </div>
    </Link>
  )
}

function badgeFor(item: PnLItem, data: PnLSummaryOut): string | null {
  if (item.order_id === data.best_order_id) return translate('pnl.tagBest')
  if (item.order_id === data.worst_order_id && data.best_order_id !== data.worst_order_id) return translate('pnl.tagWorst')
  return null
}

function Stat({ label, value, profit }: { label: string; value: string; profit?: number }) {
  const tone =
    profit == null ? 'text-slate-100' : profit > 0 ? 'text-profit' : profit < 0 ? 'text-loss' : 'text-slate-100'
  return (
    <div className="rounded-lg border border-surface-border bg-surface-raised p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={['num mt-1 text-lg', tone].join(' ')}>{value}</div>
    </div>
  )
}

function BarChart({ points }: { points: { label: string; value: number }[] }) {
  if (points.length === 0) {
    return <p className="text-sm text-slate-500">{translate('dash.noPoints')}</p>
  }
  const max = Math.max(...points.map((item) => Math.abs(item.value)), 1)
  const width = 640
  const height = 160
  const gap = 4
  const bar = Math.max(3, (width - gap * points.length) / points.length)
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-40 w-full">
      {points.map((point, index) => {
        const h = (Math.abs(point.value) / max) * (height - 24)
        const x = index * (bar + gap)
        const y = height - 16 - h
        return (
          <g key={`${point.label}-${index}`}>
            <rect x={x} y={y} width={bar} height={h} rx={1} fill={point.value < 0 ? '#f87171' : '#94a3b8'} />
            {points.length <= 24 && (
              <text x={x + bar / 2} y={height - 4} textAnchor="middle" className="fill-slate-500" fontSize="8">
                {point.label}
              </text>
            )}
          </g>
        )
      })}
    </svg>
  )
}
